// SMART App Launch Implementation
// Based on: https://build.fhir.org/ig/HL7/smart-app-launch/app-launch.html

import { SMARTConfiguration, SMARTAuthorizationResponse, SMARTWellKnownConfig, SMARTLaunchContext } from './types';

// SMART scopes for our authorization workflow
export const SMART_SCOPES = [
  'launch',
  'patient/Patient.read',
  'patient/Coverage.read',
  'patient/Condition.read',
  'patient/Encounter.read',
  'user/Practitioner.read',
  'openid',
  'fhirUser',
  'offline_access', // For refresh tokens
];

// Storage keys
const STORAGE_KEYS = {
  STATE: 'smart_auth_state',
  CODE_VERIFIER: 'smart_code_verifier',
  FHIR_BASE_URL: 'smart_fhir_base_url',
  LAUNCH_CONTEXT: 'smart_launch_context',
  ACCESS_TOKEN: 'smart_access_token',
  REFRESH_TOKEN: 'smart_refresh_token',
  TOKEN_EXPIRES_AT: 'smart_token_expires_at',
  PATIENT_ID: 'smart_patient_id',
};

/**
 * Generate a cryptographically secure random string
 */
function generateRandomString(length: number): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => chars[byte % chars.length]).join('');
}

/**
 * Generate PKCE code verifier and challenge
 * Using S256 method as required by SMART on FHIR
 */
async function generatePKCE(): Promise<{ verifier: string; challenge: string }> {
  const verifier = generateRandomString(64);
  
  // Create SHA-256 hash
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  
  // Base64URL encode
  const hashArray = new Uint8Array(hashBuffer);
  const challenge = btoa(String.fromCharCode(...hashArray))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
  
  return { verifier, challenge };
}

/**
 * Fetch SMART configuration from .well-known endpoint
 */
export async function fetchSMARTConfiguration(fhirBaseUrl: string): Promise<SMARTWellKnownConfig> {
  const wellKnownUrl = `${fhirBaseUrl}/.well-known/smart-configuration`;
  
  const response = await fetch(wellKnownUrl, {
    headers: {
      'Accept': 'application/json',
    },
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch SMART configuration: ${response.status}`);
  }
  
  return response.json();
}

/**
 * Initiate SMART App Launch (EHR Launch Flow)
 * This is called when the app is launched from Epic
 */
export async function initiateSMARTLaunch(
  issuer: string, // The iss parameter from launch URL
  launchToken: string, // The launch parameter from Epic
  clientId: string,
  redirectUri: string
): Promise<void> {
  // Fetch SMART configuration
  const smartConfig = await fetchSMARTConfiguration(issuer);
  
  // Generate PKCE challenge
  const { verifier, challenge } = await generatePKCE();
  
  // Generate state for CSRF protection
  const state = generateRandomString(32);
  
  // Store values for callback
  sessionStorage.setItem(STORAGE_KEYS.STATE, state);
  sessionStorage.setItem(STORAGE_KEYS.CODE_VERIFIER, verifier);
  sessionStorage.setItem(STORAGE_KEYS.FHIR_BASE_URL, issuer);
  
  // Build authorization URL
  const authUrl = new URL(smartConfig.authorization_endpoint);
  authUrl.searchParams.set('response_type', 'code');
  authUrl.searchParams.set('client_id', clientId);
  authUrl.searchParams.set('redirect_uri', redirectUri);
  authUrl.searchParams.set('scope', SMART_SCOPES.join(' '));
  authUrl.searchParams.set('state', state);
  authUrl.searchParams.set('aud', issuer); // FHIR server base URL
  authUrl.searchParams.set('launch', launchToken);
  authUrl.searchParams.set('code_challenge', challenge);
  authUrl.searchParams.set('code_challenge_method', 'S256');
  
  // Redirect to authorization server
  window.location.href = authUrl.toString();
}

/**
 * Handle the OAuth callback after authorization
 */
export async function handleSMARTCallback(
  code: string,
  state: string,
  clientId: string,
  redirectUri: string
): Promise<SMARTAuthorizationResponse> {
  // Verify state
  const storedState = sessionStorage.getItem(STORAGE_KEYS.STATE);
  if (state !== storedState) {
    throw new Error('Invalid state parameter - possible CSRF attack');
  }
  
  // Get stored values
  const codeVerifier = sessionStorage.getItem(STORAGE_KEYS.CODE_VERIFIER);
  const fhirBaseUrl = sessionStorage.getItem(STORAGE_KEYS.FHIR_BASE_URL);
  
  if (!codeVerifier || !fhirBaseUrl) {
    throw new Error('Missing stored SMART session data');
  }
  
  // Fetch SMART configuration for token endpoint
  const smartConfig = await fetchSMARTConfiguration(fhirBaseUrl);
  
  // Exchange code for tokens
  const tokenResponse = await fetch(smartConfig.token_endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: redirectUri,
      client_id: clientId,
      code_verifier: codeVerifier,
    }),
  });
  
  if (!tokenResponse.ok) {
    const error = await tokenResponse.text();
    throw new Error(`Token exchange failed: ${error}`);
  }
  
  const tokens: SMARTAuthorizationResponse = await tokenResponse.json();
  
  // Store tokens
  sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token);
  if (tokens.refresh_token) {
    sessionStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token);
  }
  sessionStorage.setItem(
    STORAGE_KEYS.TOKEN_EXPIRES_AT,
    String(Date.now() + tokens.expires_in * 1000)
  );
  if (tokens.patient) {
    sessionStorage.setItem(STORAGE_KEYS.PATIENT_ID, tokens.patient);
  }
  
  // Store launch context
  const launchContext: SMARTLaunchContext = {
    patient: tokens.patient || '',
    encounter: tokens.encounter,
    fhirUser: tokens.fhirUser,
  };
  sessionStorage.setItem(STORAGE_KEYS.LAUNCH_CONTEXT, JSON.stringify(launchContext));
  
  // Clean up PKCE data
  sessionStorage.removeItem(STORAGE_KEYS.STATE);
  sessionStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);
  
  return tokens;
}

/**
 * Refresh the access token using refresh token
 */
export async function refreshAccessToken(
  clientId: string
): Promise<SMARTAuthorizationResponse | null> {
  const refreshToken = sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  const fhirBaseUrl = sessionStorage.getItem(STORAGE_KEYS.FHIR_BASE_URL);
  
  if (!refreshToken || !fhirBaseUrl) {
    return null;
  }
  
  const smartConfig = await fetchSMARTConfiguration(fhirBaseUrl);
  
  const tokenResponse = await fetch(smartConfig.token_endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: clientId,
    }),
  });
  
  if (!tokenResponse.ok) {
    clearSMARTSession();
    return null;
  }
  
  const tokens: SMARTAuthorizationResponse = await tokenResponse.json();
  
  // Update stored tokens
  sessionStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token);
  if (tokens.refresh_token) {
    sessionStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token);
  }
  sessionStorage.setItem(
    STORAGE_KEYS.TOKEN_EXPIRES_AT,
    String(Date.now() + tokens.expires_in * 1000)
  );
  
  return tokens;
}

/**
 * Get current access token, refreshing if needed
 */
export async function getAccessToken(clientId: string): Promise<string | null> {
  const accessToken = sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  const expiresAt = sessionStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
  
  if (!accessToken) {
    return null;
  }
  
  // Check if token is expired (with 5 minute buffer)
  if (expiresAt && Date.now() > parseInt(expiresAt) - 300000) {
    const refreshed = await refreshAccessToken(clientId);
    return refreshed?.access_token || null;
  }
  
  return accessToken;
}

/**
 * Get the current SMART launch context
 */
export function getSMARTLaunchContext(): SMARTLaunchContext | null {
  const contextStr = sessionStorage.getItem(STORAGE_KEYS.LAUNCH_CONTEXT);
  if (!contextStr) return null;
  
  try {
    return JSON.parse(contextStr);
  } catch {
    return null;
  }
}

/**
 * Get the FHIR base URL from session
 */
export function getFHIRBaseUrl(): string | null {
  return sessionStorage.getItem(STORAGE_KEYS.FHIR_BASE_URL);
}

/**
 * Check if we have an active SMART session
 */
export function hasSMARTSession(): boolean {
  const accessToken = sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  const expiresAt = sessionStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
  
  if (!accessToken || !expiresAt) return false;
  
  // Check if completely expired (no buffer - can still try refresh)
  return Date.now() < parseInt(expiresAt);
}

/**
 * Clear the SMART session
 */
export function clearSMARTSession(): void {
  Object.values(STORAGE_KEYS).forEach(key => {
    sessionStorage.removeItem(key);
  });
}

/**
 * Parse SMART launch parameters from URL
 */
export function parseSMARTLaunchParams(
  searchParams: URLSearchParams
): { iss?: string; launch?: string; code?: string; state?: string } {
  return {
    iss: searchParams.get('iss') || undefined,
    launch: searchParams.get('launch') || undefined,
    code: searchParams.get('code') || undefined,
    state: searchParams.get('state') || undefined,
  };
}
